import React from 'react';
import { FaMoneyBillWave, FaWallet, FaExclamationTriangle, FaCheckCircle, FaCreditCard, FaChartLine, FaChartBar, FaSpinner } from 'react-icons/fa';
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';

export default function DashboardFinancial({
  financialStats, revenueHistory, reconciliationHistory, classWiseCollection, dailyCollection, topDefaulters, selectedMonth, selectedYear, handleMonthYearChange, formatCurrency, COLORS, today, filterLoading
}) {
  return (
    <div className="dashboard-content" style={{ position: 'relative' }}>
      {filterLoading && (
        <div className="financial-loading-overlay">
          <FaSpinner className="fa-spin" />
          <span>Updating...</span>
        </div>
      )}
      {/* Financial Metrics */}
      <div className="metrics-grid">
        <div className="metric-card financial">
          <div className="metric-icon"><FaMoneyBillWave /></div>
          <div className="metric-info">
            <p className="metric-label">Total Revenue (This Month)</p>
            <h2 className="metric-value">{formatCurrency(financialStats?.totalRevenue || 0)}</h2>
          </div>
        </div>
        <div className="metric-card success">
          <div className="metric-icon"><FaWallet /></div>
          <div className="metric-info">
            <p className="metric-label">Today's Collection</p>
            <h2 className="metric-value">{formatCurrency(financialStats?.todaysCollection || 0)}</h2>
          </div>
        </div>
        <div className="metric-card warning">
          <div className="metric-icon"><FaExclamationTriangle /></div>
          <div className="metric-info">
            <p className="metric-label">Fees Pending</p>
            <h2 className="metric-value">{formatCurrency(financialStats?.outstandingDues || 0)}</h2>
          </div>
        </div>
        <div className="metric-card success">
          <div className="metric-icon"><FaCheckCircle /></div>
          <div className="metric-info">
            <p className="metric-label">Advance Payments</p>
            <h2 className="metric-value">{formatCurrency(financialStats?.advancePayments || 0)}</h2>
          </div>
        </div>
        <div className="metric-card info">
          <div className="metric-icon"><FaCreditCard /></div>
          <div className="metric-info">
            <p className="metric-label">Today's Total Transactions</p>
            <h2 className="metric-value">{financialStats?.totalTransactions || 0}</h2>
          </div>
        </div>
        <div className="metric-card danger">
          <div className="metric-icon"><FaChartBar /></div>
          <div className="metric-info">
            <p className="metric-label">Total Discount (This Month)</p>
            <h2 className="metric-value">{formatCurrency(financialStats?.totalDiscount || 0)}</h2>
          </div>
        </div>
      </div>
      {/* Financial Charts */}
      <div className="charts-row">
        <div className="chart-card large">
          <div className="chart-header">
            <h3><FaChartLine /> Daily Collection ({new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' })})</h3>
          </div>
          <div className="chart-body">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dailyCollection}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ecf0f1" />
                <XAxis dataKey="day" stroke="#7f8c8d" />
                <YAxis stroke="#7f8c8d" />
                <Tooltip formatter={value => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="collection" fill="#3498db" name="Collection (₹)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="chart-card">
          <div className="chart-header">
            <h3><FaCreditCard /> Payment Methods</h3>
          </div>
          <div className="chart-body">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={Object.entries(financialStats?.paymentMethods || {}).map(([method, amount]) => ({
                    name: method,
                    value: amount
                  }))}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {Object.keys(financialStats?.paymentMethods || {}).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={value => formatCurrency(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="charts-row-custom">
        <div className="charts-column-left">
          <div className="chart-card">
            <div className="chart-header">
              <h3><FaChartBar /> Class-wise Fee Collection ({new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' })})</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={classWiseCollection}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ecf0f1" />
                  <XAxis dataKey="class" stroke="#7f8c8d" />
                  <YAxis stroke="#7f8c8d" />
                  <Tooltip formatter={value => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="amount" fill="#2ecc71" name="Collection (₹)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="chart-card">
            <div className="chart-header">
              <h3><FaChartLine /> Revenue & Students History (Academic Session - March to March)</h3>
            </div>
            <div className="chart-body">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={reconciliationHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ecf0f1" />
                  <XAxis dataKey="month" stroke="#7f8c8d" angle={-45} textAnchor="end" height={80} />
                  <YAxis stroke="#7f8c8d" />
                  <YAxis yAxisId="right" orientation="right" stroke="#2ecc71" />
                  <Tooltip formatter={(value, name) => name === 'students' ? value : formatCurrency(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" stroke="#3498db" strokeWidth={3} dot={{ fill: '#3498db', r: 6 }} name="Revenue (₹)" />
                  <Line type="monotone" dataKey="students" stroke="#2ecc71" strokeWidth={3} dot={{ fill: '#2ecc71', r: 6 }} name="Students" yAxisId="right" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="charts-column-right">
          <div className="chart-card chart-full-height">
            <div className="chart-header">
              <h3><FaExclamationTriangle /> Top 10 Defaulters</h3>
            </div>
            <div className="chart-body">
              <div className="defaulters-full-list">
                {topDefaulters.map((student, index) => (
                  <div key={student.rno} className="defaulter-row">
                    <span className="defaulter-rank">#{index + 1}</span>
                    <div className="defaulter-info-full">
                      <span className="defaulter-name">{student.name}</span>
                      <span className="defaulter-meta">Roll No: {student.rno} | Class: {student.class}</span>
                    </div>
                    <span className="defaulter-amount-large">{formatCurrency(student.dues)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
